import React, {
  forwardRef,
  type SelectHTMLAttributes,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { Option, type OptionProps } from "@mui/base/Option";
import { Select } from "@mui/base/Select";
import type { SelectOption } from "@mui/base/useOption";
import type { SelectChangeEventType, SelectValue } from "@mui/base/useSelect";
import classNames from "classnames";

import { ReactComponent as Icon_TriangleDown } from "../../assets/icons/TriangleDown.svg";

import style from "./CustomSelect.module.css";

export type OptionValue = string | number;
export type SelectValueType = SelectValue<OptionValue, boolean>;

interface CustomSelectProps {
  value?: SelectValueType;
  defaultValue?: SelectValueType;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onChange?: (event: SelectChangeEventType, value: SelectValueType) => void;
  onListboxOpenChange?: (isOpen: boolean) => void;
  renderValue?: (
    option: SelectOption<OptionValue> | SelectOption<OptionValue>[] | null
  ) => React.ReactNode;
  alignSelectedWithTrigger?: boolean;
  error?: boolean;
  errorMessage?: string;
  className?: string;
  style?: React.CSSProperties;
  placeholder?: string;
  children?: React.ReactNode;
  disabled?: boolean;
  required?: boolean;
  hideIcon?: boolean;
}

const POPUP_PADDING_PX = 8;

function setRef<T>(ref: React.Ref<T> | undefined, value: T | null) {
  if (!ref) return;
  if (typeof ref === "function") ref(value);
  else (ref as React.MutableRefObject<T | null>).current = value;
}

function findSelectedElement(listbox: HTMLUListElement): HTMLElement | null {
  return (
    listbox.querySelector<HTMLElement>('[aria-selected="true"]') ??
    listbox.querySelector<HTMLElement>(".base--selected")
  );
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(value, max));
}

function readTranslateXY(transform: string) {
  if (!transform || transform === "none") return { x: 0, y: 0 };
  const m = new DOMMatrixReadOnly(transform);
  return { x: m.m41, y: m.m42 };
}

function alignPopupToSelected(params: {
  listbox: HTMLUListElement;
  trigger: HTMLButtonElement;
  popup: HTMLElement;
}) {
  const { listbox, trigger, popup } = params;

  const selected = findSelectedElement(listbox);
  if (!selected) return;

  const triggerRect = trigger.getBoundingClientRect();
  const selectedRect = selected.getBoundingClientRect();

  const triggerCenterY = triggerRect.top + triggerRect.height / 2;
  const selectedCenterY = selectedRect.top + selectedRect.height / 2;

  const deltaToCenter = selectedCenterY - triggerCenterY;
  const maxScroll = Math.max(0, listbox.scrollHeight - listbox.clientHeight);
  listbox.scrollTop = clamp(listbox.scrollTop + deltaToCenter, 0, maxScroll);

  const alignedRect = selected.getBoundingClientRect();
  const alignedCenterY = alignedRect.top + alignedRect.height / 2;
  const visualDeltaY = alignedCenterY - triggerCenterY;

  const { x: currentX, y: currentY } = readTranslateXY(window.getComputedStyle(popup).transform);

  let nextY = currentY - visualDeltaY;

  const popupRect = popup.getBoundingClientRect();
  const futureTop = popupRect.top - visualDeltaY;
  const futureBottom = futureTop + popupRect.height;

  const minTop = POPUP_PADDING_PX;
  const maxBottom = window.innerHeight - POPUP_PADDING_PX;

  if (futureTop < minTop) nextY += minTop - futureTop;
  else if (futureBottom > maxBottom) nextY += maxBottom - futureBottom;

  popup.style.transform = `translate3d(${currentX}px, ${nextY}px, 0)`;
}

interface CustomSelectOwnerState extends CustomSelectProps {
  open?: boolean;
}

type ButtonSlotProps = React.ComponentPropsWithoutRef<"button"> & {
  hideIcon?: boolean;
  ownerState?: CustomSelectOwnerState;
};

const SelectButton = forwardRef<HTMLButtonElement, ButtonSlotProps>(function SelectButton(
  { hideIcon, children, ownerState: _ownerState, ...buttonProps },
  ref
) {
  return (
    <button type="button" ref={ref} {...buttonProps}>
      <div className={style.buttonText}>
        {children}
        {!hideIcon && <Icon_TriangleDown className={style.icon} />}
      </div>
    </button>
  );
});

type ListboxSlotProps = React.HTMLAttributes<HTMLUListElement> & {
  ownerState?: { open?: boolean };
  alignSelectedWithTrigger?: boolean;
  triggerRef?: React.RefObject<HTMLButtonElement>;
  selectionKey?: unknown;
};

type PopupSlotProps = React.HTMLAttributes<HTMLDivElement> & {
  ownerState?: { open?: boolean };
};

const SelectPopup = forwardRef<HTMLDivElement, PopupSlotProps>(function SelectPopup(
  { ownerState, style: popupStyle, ...popupProps },
  ref
) {
  const isOpen = Boolean(ownerState?.open);
  const nextStyle = {
    ...popupStyle,
    ...(!isOpen && { display: "none" }),
  };

  return <div ref={ref} {...popupProps} style={nextStyle} />;
});

const AligningListbox = forwardRef<HTMLUListElement, ListboxSlotProps>(
  function AligningListbox(props, forwardedRef) {
    const { ownerState, alignSelectedWithTrigger, triggerRef, selectionKey, ...ulProps } = props;

    const localListboxRef = useRef<HTMLUListElement | null>(null);
    const [listboxKey, setListboxKey] = useState(0);

    const setListboxRef = useCallback(
      (node: HTMLUListElement | null) => {
        localListboxRef.current = node;
        setRef(forwardedRef, node);
      },
      [forwardedRef]
    );

    const isOpen = Boolean(ownerState?.open);

    // Remount once on open so compound items re-register in DOM order. Caused issues with cursor key navigation.
    useLayoutEffect(() => {
      if (!alignSelectedWithTrigger || !isOpen || listboxKey > 0) return;
      setListboxKey(1);
    }, [alignSelectedWithTrigger, isOpen, listboxKey]);

    useLayoutEffect(() => {
      if (!alignSelectedWithTrigger || !isOpen) return;

      const listbox = localListboxRef.current;
      const trigger = triggerRef?.current;
      const popup = listbox?.parentElement as HTMLElement | null;

      if (!listbox || !trigger || !popup) return;

      const originalInlineTransform = popup.style.transform;

      const raf = window.requestAnimationFrame(() => {
        alignPopupToSelected({ listbox, trigger, popup });
      });

      return () => {
        window.cancelAnimationFrame(raf);
        popup.style.transform = originalInlineTransform;
      };
    }, [alignSelectedWithTrigger, isOpen, listboxKey, selectionKey, triggerRef]);

    return <ul key={listboxKey} {...ulProps} ref={setListboxRef} />;
  }
);

const InternalCustomSelect = forwardRef<
  HTMLButtonElement,
  CustomSelectProps &
    Omit<SelectHTMLAttributes<HTMLButtonElement>, "defaultValue" | "onChange" | "value">
>((props, forwardedRef) => {
  const {
    alignSelectedWithTrigger = false,
    hideIcon = false,
    onClick,
    onListboxOpenChange,

    error,
    errorMessage,

    className,
    style: inlineStyle,
    placeholder,

    ...selectProps
  } = props;

  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const setTriggerRef = useCallback(
    (node: HTMLButtonElement | null) => {
      triggerRef.current = node;
      setRef(forwardedRef, node);
    },
    [forwardedRef]
  );

  const selectionKey = selectProps.value ?? selectProps.defaultValue ?? null;
  const hasError = Boolean(error || errorMessage);

  const rootClassName = classNames(
    style.select,
    hasError && style.error,
    hideIcon && style.hideIcon,
    className
  );

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(event);
    },
    [onClick]
  );

  return (
    <>
      <Select<OptionValue, boolean>
        {...selectProps}
        onListboxOpenChange={onListboxOpenChange}
        ref={setTriggerRef}
        slots={{ root: SelectButton, listbox: AligningListbox, popup: SelectPopup }}
        className={rootClassName}
        slotProps={{
          root: {
            className: rootClassName,
            hideIcon,
            onClick: handleClick,
          } as ButtonSlotProps,
          popup: {
            style: { zIndex: 100000 },
          },
          listbox: {
            className: classNames(style.selectListbox, className),
            alignSelectedWithTrigger,
            triggerRef,
            selectionKey,
            ...(inlineStyle?.width && { style: { width: inlineStyle.width } }),
          } as ListboxSlotProps,
        }}
        placeholder={<span className={style.placeholder}>{placeholder}</span>}
      />
      {errorMessage && (
        <div className={style.errorMessage}>
          <span>{errorMessage}</span>
        </div>
      )}
    </>
  );
});
InternalCustomSelect.displayName = "CustomSelect";

const InternalOption = ({ className, ...props }: OptionProps<OptionValue>) => {
  return <Option {...props} className={classNames(style.selectOption, className)} />;
};

interface ICustomSelect extends React.ForwardRefExoticComponent<
  CustomSelectProps & React.RefAttributes<HTMLButtonElement>
> {
  Option: typeof InternalOption;
}

export const CustomSelect = Object.assign(InternalCustomSelect, {
  Option: InternalOption,
}) as ICustomSelect;
